// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { SNF_PHASES, admitBridge, bridgeEndpoint, bridgeId, defineBridgeWitness, defineSeam, isNeutralizationTheoremCode, openConsumption, witnessKindForTheorem, } from "@csk/core";
import { WASM_THEOREM_META } from "./meta.js";
import { squareIsForbidden } from "./squares.js";
function requireTheoremCode(value) {
    if (isNeutralizationTheoremCode(value))
        return value;
    throw new Error(`Unknown theorem code: ${value}`);
}
function seamDescriptorForState(code) {
    const meta = WASM_THEOREM_META[code];
    if (meta === undefined)
        throw new Error(`Unknown theorem code: ${code}`);
    return {
        theoremCode: code,
        theoremName: meta.name,
        seamOrientation: meta.seamType,
        forbiddenGenerator: meta.generator,
        requiredWitnessKind: witnessKindForTheorem(code),
    };
}
function requireNonEmpty(value, name) {
    const trimmed = value.trim();
    if (trimmed.length === 0)
        throw new Error(`${name} cannot be empty`);
    return trimmed;
}
function requirePositiveInteger(value, name) {
    if (!Number.isSafeInteger(value) || value < 1) {
        throw new Error(`${name} must be a safe integer >= 1`);
    }
    return value;
}
function parseSeamNormalFormTraceJson(phasesJson) {
    const value = JSON.parse(phasesJson);
    if (Array.isArray(value)) {
        return { phases: value };
    }
    if (typeof value === "object" && value !== null) {
        const phases = value["phases"];
        if (Array.isArray(phases))
            return { phases };
    }
    throw new Error("SNF trace must be a JSON array or an object with phases");
}
function outcomeJson(outcome) {
    return JSON.stringify(outcome);
}
function rejectedOutcome(bridgeIdValue, status, reason) {
    return outcomeJson({
        status,
        bridgeId: bridgeIdValue,
        reason,
        receipt: null,
    });
}
function statefulBridgeFor(bridgeIdValue, bridge, seam, witnessRecord) {
    const witness = witnessRecord === null
        ? null
        : {
            ...witnessRecord.witness,
            revoked: witnessRecord.revoked,
        };
    return {
        id: bridgeId(`wasm-state-bridge:${String(bridgeIdValue)}`),
        from: bridgeEndpoint("source", bridge.source, {
            lane: "Zone1",
            domain: "typescript",
        }),
        to: bridgeEndpoint("target", bridge.target, {
            lane: "Zone2",
            domain: "wasm",
        }),
        seam: seam.seam,
        witness,
        phaseCoverage: SNF_PHASES,
        lifecycle: bridge.lifecycle,
        consumption: witnessRecord?.consumption ?? null,
        receipt: null,
    };
}
function receiptForStatefulAdmission(bridgeIdValue, seamId, bridge, witnessId, witness, scope, result) {
    return {
        bridgeId: bridgeIdValue,
        seamId,
        witnessId,
        theoremCode: witness.witness.guarantee.code,
        witnessKind: witness.witness.kind,
        source: bridge.source,
        target: bridge.target,
        scope,
        lifecycle: "admitted",
        phases: result.receipt.trace.phases,
        consumedResources: result.receipt.consumedResources,
        previousAnchor: result.receipt.previousAnchor,
        nextAnchor: result.receipt.nextAnchor,
    };
}
export function createStatefulSeamBridgeContract() {
    let nextId = 1;
    const allocateId = () => {
        const id = requirePositiveInteger(nextId, "registry id");
        nextId = id + 1;
        return id;
    };
    const seams = new Map();
    const witnesses = new Map();
    const bridges = new Map();
    const admitBridgeWithSquareFlag = (bridge_id, scope, phases_json, forbidden_square) => {
        const bridgeId = requirePositiveInteger(bridge_id, "bridge_id");
        const bridge = bridges.get(bridgeId);
        if (bridge === undefined) {
            throw new Error(`bridge ${String(bridgeId)} does not exist`);
        }
        if (bridge.lifecycle !== "proposed") {
            return rejectedOutcome(bridgeId, "rejected-invalid-lifecycle", `bridge lifecycle is ${bridge.lifecycle}`);
        }
        const seam = seams.get(bridge.seamId);
        if (seam === undefined) {
            throw new Error(`seam ${String(bridge.seamId)} does not exist`);
        }
        const witnessRecord = bridge.witnessId === null
            ? null
            : (witnesses.get(bridge.witnessId) ?? null);
        if (bridge.witnessId !== null && witnessRecord === null) {
            throw new Error(`bridge witness ${String(bridge.witnessId)} does not exist`);
        }
        const trace = parseSeamNormalFormTraceJson(phases_json);
        const cskBridge = statefulBridgeFor(bridgeId, bridge, seam, witnessRecord);
        const context = {
            scope: requireNonEmpty(scope, "bridge admission scope"),
            forbiddenSquare: forbidden_square,
            now: 2,
            ...(witnessRecord !== null
                ? { consumption: witnessRecord.consumption }
                : {}),
            receipt: {
                consumedResources: [
                    `seam:${String(bridge.seamId)}`,
                    ...(bridge.witnessId === null
                        ? []
                        : [`witness:${String(bridge.witnessId)}`]),
                ],
                trace,
            },
        };
        const result = admitBridge(cskBridge, context);
        if (witnessRecord !== null && result.consumption !== null) {
            witnessRecord.consumption = result.consumption;
        }
        if (result.verdict.status !== "admitted") {
            bridge.lifecycle = "rejected";
            const reason = result.verdict.status === "rejected-missing-witness"
                ? `missing ${result.verdict.required}`
                : result.verdict.reason;
            return rejectedOutcome(bridgeId, result.verdict.status, reason);
        }
        if (bridge.witnessId === null || witnessRecord === null) {
            throw new Error("admitted bridge is missing witness state");
        }
        bridge.lifecycle = "admitted";
        const receipt = receiptForStatefulAdmission(bridgeId, bridge.seamId, bridge, bridge.witnessId, witnessRecord, context.scope, result.verdict);
        bridge.receipt = receipt;
        return outcomeJson({
            status: "admitted",
            bridgeId,
            reason: null,
            receipt,
        });
    };
    return {
        seam_define(theorem_code, forbidden_pattern) {
            const theoremCode = requireTheoremCode(theorem_code);
            const descriptor = seamDescriptorForState(theoremCode);
            const id = allocateId();
            const seam = defineSeam({
                id: `wasm-state-seam:${String(id)}:${theoremCode}`,
                orientation: descriptor.seamOrientation,
                theorem: theoremCode,
                forbiddenPattern: requireNonEmpty(forbidden_pattern, "forbidden pattern"),
            });
            seams.set(id, { seam });
            return id;
        },
        bridge_witness_mint(theorem_code, scope, max_uses) {
            const theoremCode = requireTheoremCode(theorem_code);
            const id = allocateId();
            const witness = defineBridgeWitness({
                id: `wasm-state-witness:${String(id)}:${theoremCode}`,
                theorem: theoremCode,
                issuer: "wasm-seam-bridge",
                scope: requireNonEmpty(scope, "bridge witness scope"),
                issuedAt: 1,
            });
            const maxUses = requirePositiveInteger(max_uses, "max_uses");
            witnesses.set(id, {
                witness,
                maxUses,
                consumption: openConsumption(witness.id, maxUses),
                revoked: false,
            });
            return id;
        },
        bridge_propose(seam_id, witness_id, source, target) {
            const seamId = requirePositiveInteger(seam_id, "seam_id");
            const witnessIdValue = witness_id === 0 ? 0 : requirePositiveInteger(witness_id, "witness_id");
            const seam = seams.get(seamId);
            if (seam === undefined)
                throw new Error(`seam ${String(seamId)} does not exist`);
            const witnessId = witnessIdValue === 0
                ? null
                : witnesses.has(witnessIdValue)
                    ? witnessIdValue
                    : undefined;
            if (witnessId === undefined) {
                throw new Error(`bridge witness ${String(witnessIdValue)} does not exist`);
            }
            const witnessRecord = witnessId === null ? null : witnesses.get(witnessId);
            if (witnessRecord !== null &&
                witnessRecord !== undefined &&
                witnessRecord.witness.guarantee.code !== seam.seam.theorem.code) {
                throw new Error(`bridge witness theorem ${witnessRecord.witness.guarantee.code} does not match seam theorem ${seam.seam.theorem.code}`);
            }
            const id = allocateId();
            bridges.set(id, {
                seamId,
                witnessId,
                source: requireNonEmpty(source, "bridge source"),
                target: requireNonEmpty(target, "bridge target"),
                lifecycle: "proposed",
                receipt: null,
            });
            return id;
        },
        bridge_admit(bridge_id, scope, phases_json, forbidden_square) {
            const bridgeId = requirePositiveInteger(bridge_id, "bridge_id");
            const bridge = bridges.get(bridgeId);
            if (bridge === undefined) {
                throw new Error(`bridge ${String(bridgeId)} does not exist`);
            }
            const seam = seams.get(bridge.seamId);
            if (seam === undefined) {
                throw new Error(`seam ${String(bridge.seamId)} does not exist`);
            }
            return admitBridgeWithSquareFlag(bridgeId, scope, phases_json, forbidden_square);
        },
        bridge_admit_square(bridge_id, scope, phases_json, generator_code) {
            const bridgeId = requirePositiveInteger(bridge_id, "bridge_id");
            const bridge = bridges.get(bridgeId);
            if (bridge === undefined) {
                throw new Error(`bridge ${String(bridgeId)} does not exist`);
            }
            const seam = seams.get(bridge.seamId);
            if (seam === undefined) {
                throw new Error(`seam ${String(bridge.seamId)} does not exist`);
            }
            return admitBridgeWithSquareFlag(bridgeId, scope, phases_json, squareIsForbidden(seam.seam.theorem.code, generator_code));
        },
        bridge_receipt(bridge_id) {
            const bridgeId = requirePositiveInteger(bridge_id, "bridge_id");
            const receipt = bridges.get(bridgeId)?.receipt ?? null;
            if (receipt === null) {
                throw new Error(`bridge ${String(bridgeId)} has not emitted a receipt`);
            }
            return JSON.stringify(receipt);
        },
        bridge_lifecycle(bridge_id) {
            const bridgeId = requirePositiveInteger(bridge_id, "bridge_id");
            const lifecycle = bridges.get(bridgeId)?.lifecycle;
            if (lifecycle === undefined) {
                throw new Error(`bridge ${String(bridgeId)} does not exist`);
            }
            return lifecycle;
        },
        bridge_witness_remaining_uses(witness_id) {
            const witnessId = requirePositiveInteger(witness_id, "witness_id");
            const witness = witnesses.get(witnessId);
            if (witness === undefined) {
                throw new Error(`bridge witness ${String(witnessId)} does not exist`);
            }
            if (witness.revoked)
                return 0;
            return Math.max(0, witness.maxUses - witness.consumption.uses);
        },
        bridge_revoke_witness(witness_id) {
            const witnessId = requirePositiveInteger(witness_id, "witness_id");
            const witness = witnesses.get(witnessId);
            if (witness === undefined) {
                throw new Error(`bridge witness ${String(witnessId)} does not exist`);
            }
            witness.revoked = true;
        },
    };
}
//# sourceMappingURL=seam-bridge-state.js.map